
import { numericFormatter } from 'react-number-format';
import { showToast as showToastUI } from '../helpers/toast';
import { appStore } from '../store/jotaiStore';
import { userIdAtom, userNameAtom, userEmailAtom } from '../store/company';
import { withCostMoves } from './costMoves';

// Pull the logged-in user's identity from the jotai store at request time.
// Used to inject into updateUserDataArray / deleteUserData payloads so the
// audit log captures who actually clicked, not the hardcoded '154045' /
// 'Seth Bailey' that most call sites still send.
//
// Must read from appStore (the same store the <Provider> in App.js uses).
// getDefaultStore() is a different store — useAtom writes inside Provider
// never reach it, so reads were silently returning empty defaults and
// blanking User_Email on every audit row.
const getCurrentUserIdentity = () => {
    try {
        const trim = (v) => (typeof v === 'string' ? v.trim() : '');
        const email = trim(appStore.get(userEmailAtom));
        const name = trim(appStore.get(userNameAtom));
        const id = trim(appStore.get(userIdAtom));
        return {
            // userId for audit: prefer email (stable, identifies the human).
            // Falls back to whatever userIdAtom holds (Forecast_User_ID for
            // legacy users) if no email is available yet.
            userId: email || id,
            userName: name,
            userEmail: email,
        };
    } catch (e) {
        return { userId: '', userName: '', userEmail: '' };
    }
};

let updateQueue = []
let timeoutId = null
let updateQueueNew = []
let timeoutIdNew = null

export const storeData = async (key, value) => {
    try {
        const jsonValue = JSON.stringify(value);
        sessionStorage.setItem(key, jsonValue);
        localStorage.setItem(key, jsonValue);
    } catch (e) {
        console.log('error storing data', e)
    }
};

export const getData = async (key) => {
    try {
        let jsonValue = sessionStorage.getItem(key);
        // If not in session storage, check local storage
        if (jsonValue == null) {
            jsonValue = localStorage.getItem(key);
        }
        return jsonValue != null ? JSON.parse(jsonValue) : null;
    } catch (e) {
        console.log('error getting data', e)
    }
};

export const loadJob = async (id) => {
    const data = await getData('selectedJob');
    if (data) {
        return data
    }
}

export const loadJobDetails = async (id) => {
    const data = await getData('selectedJobDetails');
    if (data) {
        return data
    }
}

export const loadSelectedProjectManager = async (pm) => {
    const data = await getData('selectedProjectManager');
    if (data) {
        return data
    }
}

export const loadTeam = async (id) => {
    const data = await getData('selectedTeam');
    if (data) {
        return data
    }
}

export const loadVersion = async (id) => {
    const data = await getData('selectedVersion');
    if (data) {
        return data
    }
}

export const loadTab = async (id) => {
    const data = await getData('selectedTab');
    if (data) {
        return data
    }
}

export const loadSeenBefore = async (id) => {
    const data = await getData('seenBefore');
    if (data) {
        return data
    }
}

export const saveSelectedProjectManager = async (pm) => {
    console.log('saving pm', pm)
    storeData('selectedProjectManager', pm);
}

export const saveEquipmentRates = async (job) => {
    storeData('equipmentRates', job);
}

export const saveEquipmentAssets = async (job) => {
    storeData('equipmentAssets', job);
}

export const setSelectedJobStorage = async (job) => {
    console.log('saving job', job)
    storeData('selectedJob', job);
}

export const setSelectedJobDetailsStorage = async (job) => {
    console.log('saving job', job)
    storeData('selectedJobDetails', job);
}

export const setSelectedTeamStorage = async (job) => {
    console.log('saving team', job)
    storeData('selectedTeam', job);
}

export const setSelectedVersionStorage = async (version) => {
    console.log('saving version', version)
    storeData('selectedVersion', version);
}

export const setSelectedTabStrorage = async (tab) => {
    console.log('saving tab', tab)
    storeData('selectedTab', tab);
}

// --- Project Coordinator restricted view -------------------------------------
// Project Coordinators only see the procurement tabs below, plus the Labor and
// Equipment tabs in planning-only form: inside those two tabs a PC is locked to
// the Manpower Planning / Equipment Dashboard subtab — the burn-tool cost grids
// never render for them (see the isPC checks in burnTool*.js).
// 'Dates' removed while the Dates view is hidden — re-add when it returns.
export const pcAllowedTabs = ['Materials', 'Rentals', 'Subcontractors', 'Other', 'Purchase Orders', 'Burn Tool Labor', 'Burn Tool Equipment', 'Uncommitted'];

// A user is treated as a Project Coordinator if EITHER the DB returned
// role === 'Project Coordinator', OR their email is in this static fallback list.
// The static list is essential: the login fallback (and prod before the function
// is redeployed to return roles) can authenticate a user WITHOUT a role, and
// without this list that PC would slip through and see everything.
export const projectCoordinatorEmails = [
    'tomasito.gonzalez@crossfire-llc.com',
    'pablo.valenzuela@crossfire-llc.com',
    'erik.cruz@crossfire-llc.com',
    'ariana.silva@crossfire-llc.com',
    'mark.melanson@crossfire-llc.com',
    'raymond.evans@strikeusa.com',
    'ron.lowery@crossfire-llc.com',
    'hannah.soderlind@pickettsystems.com',
    'alissa.reno@boltcompanies.com',
    'dustin.willeford@strikeusa.com',
    'chance.wright@crossfire-llc.com',
    'adam.torres@strikeusa.com',
    'cole.allison@strikeusa.com',
    'rj.olmos@strikeusa.com',
    'richard.anderson@crossfire-llc.com',
    'daniel.dayton@strikeusa.com',
].map((e) => e.toLowerCase());

export const isProjectCoordinator = (role, email) =>
    role === 'Project Coordinator' ||
    // Fallback ONLY when the DB returned no role at all (login fallback path, or
    // prod before the function is redeployed to return roles). A real non-PC role
    // from the DB stays authoritative, so you can un-restrict someone by giving
    // them any other role — but if the DB says nothing, the static list decides.
    (!role && !!email && projectCoordinatorEmails.includes(String(email).toLowerCase().trim()));

// --- Buyers ------------------------------------------------------------------
// Buyers cut and extend the POs. Their view mirrors the Assistant PM (Project
// Coordinator) view exactly — same tabs, same planning-only Labor/Equipment
// treatment (the burn tools' isPC checks include buyers). 'Buyer' is a
// Forecast_User_Role value — the column is free text, so no migration is needed,
// but somebody has to set the role on the actual buyers in PC.Forecast_Users
// before this does anything. Until then the email list below is what identifies
// them, exactly as projectCoordinatorEmails backs up the PC role.
export const buyerAllowedTabs = pcAllowedTabs;

export const buyerEmails = [
    // Populate as buyers are confirmed; the DB role takes precedence either way.
].map((e) => e.toLowerCase());

export const isBuyer = (role, email) =>
    role === 'Buyer' ||
    (!role && !!email && buyerEmails.includes(String(email).toLowerCase().trim()));

// --- Admin "View as role" preview --------------------------------------------
// Lets an admin see the app as another role without a second account. See
// previewRoleAtom (store/company.js) and the switcher in the top menu (topMenu.js).

// Roles selectable in the switcher. '' = the admin's own (real) view. Values are
// the internal role names (effectiveIsProjectCoordinator matches on them); labels
// are display-only — the PC role is shown as "Assistant PM" in the UI.
export const previewableRoles = [
    { value: '', label: 'Normal' },
    { value: 'Project Coordinator', label: 'Assistant PM' },
    { value: 'Buyer', label: 'Buyer' },
];

// Who may use the switcher. Gated on REAL identity (never the previewed role) so a
// preview can always be exited. Limited to Seth, Steve and Bernardo. Everyone in dev.
export const canPreviewRoles = (email, userId) =>
    process.env.NODE_ENV !== 'production' ||
    userId === '154045' ||
    ['seth.bailey@strikeusa.com', 'steve.horman@strikeusa.com', 'steven.horman@strikeusa.com',
        'bernardo.costa@strikeusa.com', 'bcosta@strikeusa.com']
        .includes(String(email || '').toLowerCase().trim());

// Fusion admins: the Admin tab and the version unlock control (versionUnlock.js).
// A DB role of 'Admin' in PC.Forecast_Users also counts, so a new admin needs
// no deploy. Everyone in dev.
export const fusionAdminEmails = [
    'steve.horman@strikeusa.com', 'steven.horman@strikeusa.com', 'cameron.pitre@strikeusa.com',
    'aeron.griggs@strikeusa.com', 'bernardo.costa@strikeusa.com', 'bcosta@strikeusa.com',
    'christopher.ellis@strikeusa.com',
];

export const isFusionAdmin = (userId, email, role) =>
    process.env.NODE_ENV !== 'production' ||
    userId === '154045' || userId === '147219' ||
    role === 'Admin' ||
    fusionAdminEmails.includes(String(email || '').toLowerCase().trim());

// Effective PC status honoring an active preview. When a preview role is set it is
// authoritative (so the admin can enter/exit the PC view cleanly); otherwise fall
// back to the real role/email check.
export const effectiveIsProjectCoordinator = (previewRole, role, email) =>
    previewRole ? previewRole === 'Project Coordinator' : isProjectCoordinator(role, email);

// Same preview-aware treatment for buyers.
export const effectiveIsBuyer = (previewRole, role, email) =>
    previewRole ? previewRole === 'Buyer' : isBuyer(role, email);

/**
 * The tabs a user may see, or null when they are unrestricted.
 *
 * Both restricted roles include the two procurement tabs: assistant PMs get them
 * alongside their existing procurement set, buyers get only them. Everyone else
 * (PMs, project controls, admins) is unrestricted and sees them too.
 */
export const allowedTabsForRole = (previewRole, role, email) => {
    if (effectiveIsBuyer(previewRole, role, email)) return buyerAllowedTabs;
    if (effectiveIsProjectCoordinator(previewRole, role, email)) return pcAllowedTabs;
    return null;
};

// --- Project Controls Leads (PCLs) -------------------------------------------
// PCLs can lock a job's forecast on the LIVE version after the review meeting so
// it stays frozen (no edits) until the PSR produces a new version. The email list
// is authoritative because PC.Forecast_Users doesn't yet carry a PCL role; when a
// 'Project Controls Lead' role is added to the DB it's honored too. See the lock
// button in topMenu.js and the lock read in main.js (Forecast_Field_Name
// 'versionLocked' on the 'All' tab).
export const projectControlsLeadEmails = [
    'seth.bailey@strikeusa.com',
    'steve.horman@strikeusa.com',
    'steven.horman@strikeusa.com',
    'aeron.griggs@strikeusa.com',
    'cameron.pitre@strikeusa.com',
    'meghan.marasco-robertson@strikeusa.com',
    'meghan.marasco-robertson@crossfire-llc.com',
    'gerald.buckmiller@strikeusa.com',
    'joshua.daley@strikeusa.com',
    // Chris's AD UPN (what MSAL reports) is christopher.*, work email is chris.*;
    // the UPN is the one login actually sees, keep both to be safe.
    'christopher.ellis@strikeusa.com',
    'chris.ellis@strikeusa.com',
    'hugo.guel@strikeusa.com',
].map((e) => e.toLowerCase());

export const isProjectControlsLead = (role, email) =>
    role === 'Project Controls Lead' ||
    (!!email && projectControlsLeadEmails.includes(String(email).toLowerCase().trim()));

// opts.signal: an AbortController's signal; a cancelled call comes back undefined, like a failed one
const postRequest = async (url, data, opts = {}) => {
    return await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-ZUMO-AUTH': data?.auth
        },
        body: JSON.stringify(data),
        signal: opts.signal
    }).then(async (res) => {
        const data = await res.json();
        return data
    }).catch(err => {
        if (err?.name !== 'AbortError') console.log('err', err)
    })
}

const postRequestZip = async (url, data) => {
    return await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-ZUMO-AUTH': data?.auth
        },
        body: JSON.stringify(data)
    }).then(async (res) => {
        const data = await res.arrayBuffer();
        const parsedData = JSON.parse(new TextDecoder().decode(new Uint8Array(data)));

        return parsedData
    }).catch(err => {
        console.log('err from request zip', err)
    })
}

// console.log('NODE_ENV', process.env.NODE_ENV)
// console.log('REACT_APP_ENV', process.env.REACT_APP_ENV)

// To use local server: set REACT_APP_API_URL=http://localhost:5555/ in .env,
// or point the dev fallback below at http://localhost:5555/ (the local Express
// mirror in localserver/server.js — Windows auth straight to muleprod, handy
// when the Azure services are down).
const url = process.env.REACT_APP_API_URL || (process.env.NODE_ENV == 'production' ?
    (process.env.REACT_APP_ENV == 'test' || process.env.REACT_APP_ENV == 'dev') ? 'https://forecastingtestservice.azurewebsites.net/' : 'https://forecastingprodservice.azurewebsites.net/' : 'https://forecastingprodservice.azurewebsites.net/')

export const apiUrl = url

// Closed-job dispatch flag — when true, the four single-job data loaders
// (loadUserData, loadJobBillings, loadCostHistory, loadCostDetailCondensed)
// route to their PC-schema variants. Set by topMenu when a job is picked
// from the closed-job dropdown; cleared when picked from the open list.
let _closedJobMode = false;
export const setClosedJobMode = (flag) => { _closedJobMode = !!flag; };
export const isClosedJobMode = () => _closedJobMode;

export const loadFTMSInvoices = async (data) => {
    //console.log('read billings dw', data)
    return await postRequest(url + 'api/readFTMSInvoices', data)
}

export const loadFTMSTickets = async (data) => {
    return await postRequest(url + 'api/readFTMSTickets', data)
}

export const loadEquipmentData = async (data) => {
    // Separate cache namespace for closed-job (PC) mode so the two
    // versions don't poison each other.
    const cacheKey = _closedJobMode
        ? 'equipmentRates_PC_latest'
        : `equipmentRates_${data?.forecastGuid || 'default'}`
    const cached = localStorage.getItem(cacheKey)
    if (cached) {
        try {
            const parsed = JSON.parse(cached)
            if (parsed.data?.length && (Date.now() - parsed.timestamp) < 3600000) {
                return { data: parsed.data }
            }
        } catch (e) { }
    }
    const endpoint = _closedJobMode ? 'api/readEquipmentRatesPC' : 'api/readEquipmentRates';
    const result = await postRequest(url + endpoint, data)
    if (result?.data?.length) {
        try { localStorage.setItem(cacheKey, JSON.stringify({ data: result.data, timestamp: Date.now() })) } catch (e) { }
    }
    return result
}

export const loadOverheadData = async (data) => {
    const endpoint = _closedJobMode ? 'api/readOverheadPC' : 'api/readOverhead';
    return await postRequest(url + endpoint, data)
}

export const loadVariance = async (data) => {
    return await postRequest(url + 'api/readVariance', data)
}

export const loadRework = async (data) => {
    return await postRequest(url + 'api/readRework', data)
}

export const loadTicketLineDetail = async (data) => {
    return await postRequest(url + 'api/readTicketLineDetail', data)
}

export const loadTicketLineDetailOriginal = async (data) => {
    return await postRequest(url + 'api/readTicketLineDetailOriginal', data)
}

export const loadJobCostHistoryLive = async (data) => {
    return withCostMoves(await postRequest(url + 'api/readJobCostHistoryLive', data))
}

export const loadRelatedBUs = async (data) => {
    return await postRequest(url + 'api/readRelatedBUs', data)
}

export const loadARServiceBillingConsolidated = async (data) => {
    return await postRequest(url + 'api/readARServiceBillingConsolidated', data)
}

export const loadUserDataPrevious = async (data) => {
    return await postRequest(url + 'api/readUserDataPrevious', data)
}

// The forecast versions that hold saved rows for one job ({ id }), oldest
// first, with counts of what each carries (burnRows, dayZeroRows, totalRows, ...).
export const loadJobVersions = async (data) => {
    return await postRequest(url + 'api/readJobVersions', data)
}

export const loadUserData = async (data) => {
    const endpoint = _closedJobMode ? 'api/readUserDataPC' : 'api/readUserData';
    const response = await postRequest(url + endpoint, data);

    if (response?.data) {
        // Log raw 100101 Burn Tool Labor Phased rows BEFORE dedup
        // const raw100101 = response.data.filter(d => d.Forecast_Tab === 'Burn Tool Labor Phased' && d.Forecast_Row_ID && d.Forecast_Row_ID.startsWith('100101'));
        // console.log('=== RAW 100101 Burn Tool Labor Phased BEFORE dedup ===', raw100101.length, 'rows');
        // raw100101.forEach(r => console.log('  ROW:', r.Forecast_Row_ID, '| Field:', r.Forecast_Field_Name, '| Rate:', r.Rate, '| Hrs:', r.Number_Hours, '| Units:', r.Number_Units, '| Value:', r.Forecast_Value, '| ValAmt:', r.Value_Amount, '| Active:', r.Active, '| Created:', r.DSS_Created_Date, '| Updated:', r.DSS_Updated_Datetime, '| GUID:', r.Forecast_User_Data_GUID));

        // Deprioritize 'crewSize' field name: if a 'row' entry exists for the same
        // Row_ID + Tab + BU + Version, discard the 'crewSize' entry entirely
        const rowKeys = new Set(
            response.data
                .filter(d => d.Forecast_Field_Name === 'row')
                .map(d => `${d.Forecast_Tab}_${d.Forecast_Row_ID}_${d.BU_ID}_${d.Version_GUID}`)
        );
        const filtered = response.data.filter(d => {
            if (d.Forecast_Field_Name === 'crewSize') {
                const key = `${d.Forecast_Tab}_${d.Forecast_Row_ID}_${d.BU_ID}_${d.Version_GUID}`;
                return !rowKeys.has(key);
            }
            return true;
        });

        // Group data by the key fields to find duplicates
        const groupedData = filtered.reduce((acc, item) => {
            const key = `${item.Forecast_Field_Name}_${item.Forecast_Tab}_${item.Forecast_Row_ID}_${item.BU_ID}_${item.Version_GUID}`;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(item);
            return acc;
        }, {});

        // For each group, keep only the most recently created/updated record
        const deduped = Object.values(groupedData).map(group => {
            return group.reduce((latest, current) => {
                const latestDate = new Date(Math.max(
                    new Date(latest.DSS_Created_Date || 0),
                    new Date(latest.DSS_Updated_Datetime || 0)
                ));
                const currentDate = new Date(Math.max(
                    new Date(current.DSS_Created_Date || 0),
                    new Date(current.DSS_Updated_Datetime || 0)
                ));
                return currentDate > latestDate ? current : latest;
            });
        });

        // Log 100101 rows AFTER dedup
        // const deduped100101 = deduped.filter(d => d.Forecast_Tab === 'Burn Tool Labor Phased' && d.Forecast_Row_ID && d.Forecast_Row_ID.startsWith('100101'));
        // console.log('=== AFTER dedup 100101 Burn Tool Labor Phased ===', deduped100101.length, 'rows');
        // deduped100101.forEach(r => console.log('  DEDUPED ROW:', r.Forecast_Row_ID, '| Field:', r.Forecast_Field_Name, '| Rate:', r.Rate, '| Hrs:', r.Number_Hours, '| Units:', r.Number_Units, '| Value:', r.Forecast_Value, '| ValAmt:', r.Value_Amount, '| Active:', r.Active, '| Created:', r.DSS_Created_Date, '| Updated:', r.DSS_Updated_Datetime, '| GUID:', r.Forecast_User_Data_GUID));

        return {
            data: deduped
        };
    }

    return response;
}

// Raw version of loadUserData — skips crewSize filtering and dedup
// so diagnostics can see where duplicates are being created
export const loadUserDataRaw = async (data) => {
    return await postRequest(url + 'api/readUserData', data);
}

export const loadUserDataCombined = async (data) => {
    return await postRequest(url + 'api/readUserDataCombined', data)
}

export const loadPFData = async (data) => {
    return await postRequest(url + 'api/readPFData', data)
}

export const loadMaterials = async (data) => {
    return await postRequest(url + 'api/readMaterials', data)
}

export const saveMaterials = async (data) => {
    return await postRequest(url + 'api/saveMaterials', data)
}

export const loadTeamUserData = async (data) => {
    const response = await postRequest(url + 'api/readTeamUserData', data);

    if (response?.data) {
        // Deprioritize 'crewSize' field name: if a 'row' entry exists for the same
        // Row_ID + Tab + BU + Version, discard the 'crewSize' entry entirely
        const rowKeys = new Set(
            response.data
                .filter(d => d.Forecast_Field_Name === 'row')
                .map(d => `${d.Forecast_Tab}_${d.Forecast_Row_ID}_${d.BU_ID}_${d.Version_GUID}`)
        );
        const filtered = response.data.filter(d => {
            if (d.Forecast_Field_Name === 'crewSize') {
                const key = `${d.Forecast_Tab}_${d.Forecast_Row_ID}_${d.BU_ID}_${d.Version_GUID}`;
                return !rowKeys.has(key);
            }
            return true;
        });

        // Group data by the key fields to find duplicates
        const groupedData = filtered.reduce((acc, item) => {
            const key = `${item.Forecast_Field_Name}_${item.Forecast_Tab}_${item.Forecast_Row_ID}_${item.BU_ID}_${item.Version_GUID}`;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(item);
            return acc;
        }, {});

        // For each group, keep only the most recently created/updated record
        const deduped = Object.values(groupedData).map(group => {
            return group.reduce((latest, current) => {
                const latestDate = new Date(Math.max(
                    new Date(latest.DSS_Created_Date || 0),
                    new Date(latest.DSS_Updated_Datetime || 0)
                ));
                const currentDate = new Date(Math.max(
                    new Date(current.DSS_Created_Date || 0),
                    new Date(current.DSS_Updated_Datetime || 0)
                ));
                return currentDate > latestDate ? current : latest;
            });
        });

        return {
            data: deduped
        };
    }

    return response;
}

export const loadTeamUserDataMain = async (data) => {
    const response = await postRequest(url + 'api/readTeamUserDataMain', data);

    if (response?.data) {
        // Deprioritize 'crewSize' field name: if a 'row' entry exists for the same
        // Row_ID + Tab + BU + Version, discard the 'crewSize' entry entirely
        const rowKeys = new Set(
            response.data
                .filter(d => d.Forecast_Field_Name === 'row')
                .map(d => `${d.Forecast_Tab}_${d.Forecast_Row_ID}_${d.BU_ID}_${d.Version_GUID}`)
        );
        const filtered = response.data.filter(d => {
            if (d.Forecast_Field_Name === 'crewSize') {
                const key = `${d.Forecast_Tab}_${d.Forecast_Row_ID}_${d.BU_ID}_${d.Version_GUID}`;
                return !rowKeys.has(key);
            }
            return true;
        });

        // Group data by the key fields to find duplicates
        const groupedData = filtered.reduce((acc, item) => {
            const key = `${item.Forecast_Field_Name}_${item.Forecast_Tab}_${item.Forecast_Row_ID}_${item.BU_ID}_${item.Version_GUID}`;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(item);
            return acc;
        }, {});

        // For each group, keep only the most recently created/updated record
        const deduped = Object.values(groupedData).map(group => {
            return group.reduce((latest, current) => {
                const latestDate = new Date(Math.max(
                    new Date(latest.DSS_Created_Date || 0),
                    new Date(latest.DSS_Updated_Datetime || 0)
                ));
                const currentDate = new Date(Math.max(
                    new Date(current.DSS_Created_Date || 0),
                    new Date(current.DSS_Updated_Datetime || 0)
                ));
                return currentDate > latestDate ? current : latest;
            });
        });

        return {
            data: deduped
        };
    }

    return response;
}

export const loadTeamBurnToolData = async (data) => {
    const response = await postRequest(url + 'api/readTeamBurnToolData', data);

    if (response?.data) {
        // Deprioritize 'crewSize' field name: if a 'row' entry exists for the same
        // Row_ID + Tab + BU + Version, discard the 'crewSize' entry entirely
        const rowKeys = new Set(
            response.data
                .filter(d => d.Forecast_Field_Name === 'row')
                .map(d => `${d.Forecast_Tab}_${d.Forecast_Row_ID}_${d.BU_ID}_${d.Version_GUID}`)
        );
        const filtered = response.data.filter(d => {
            if (d.Forecast_Field_Name === 'crewSize') {
                const key = `${d.Forecast_Tab}_${d.Forecast_Row_ID}_${d.BU_ID}_${d.Version_GUID}`;
                return !rowKeys.has(key);
            }
            return true;
        });

        // Group data by the key fields to find duplicates
        const groupedData = filtered.reduce((acc, item) => {
            const key = `${item.Forecast_Field_Name}_${item.Forecast_Tab}_${item.Forecast_Row_ID}_${item.BU_ID}_${item.Version_GUID}`;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(item);
            return acc;
        }, {});

        // For each group, keep only the most recently created/updated record
        const deduped = Object.values(groupedData).map(group => {
            return group.reduce((latest, current) => {
                const latestDate = new Date(Math.max(
                    new Date(latest.DSS_Created_Date || 0),
                    new Date(latest.DSS_Updated_Datetime || 0)
                ));
                const currentDate = new Date(Math.max(
                    new Date(current.DSS_Created_Date || 0),
                    new Date(current.DSS_Updated_Datetime || 0)
                ));
                return currentDate > latestDate ? current : latest;
            });
        });

        return {
            data: deduped
        };
    }

    return response;
}

export const loadTeamCostDetail = async (data) => {
    return await postRequest(url + 'api/readTeamCostDetail', data)
}

export const loadTeamCostDetailProjections = async (data) => {
    return await postRequest(url + 'api/readTeamCostDetailProjections', data)
}

export const loadTeamCostDetailProjectionsAll = async (data) => {
    return await postRequest(url + 'api/readTeamCostDetailProjectionsAll', data)
}

export const loadTeamLaborHoursByWeek = async (data) => {
    return await postRequest(url + 'api/readTeamLaborHoursByWeek', data)
}

export const loadGLAccount = async (data) => {
    return await postRequest(url + 'api/readGLAccount', data)
}

export const loadGLAccountTrimmed = async (data) => {
    const ids = data.ids || [];
    const batchSize = Math.ceil(ids.length / 10);
    const batches = [];
    for (let i = 0; i < ids.length; i += batchSize) {
        batches.push(ids.slice(i, i + batchSize));
    }
    const results = await Promise.all(
        batches.map(batch => postRequest(url + 'api/readGLAccountTrimmed', { ...data, ids: batch }))
    );
    const merged = [];
    results.forEach(r => { if (r?.data) merged.push(...r.data); });
    return { data: merged };
}

// Every active JDE labor classification (company-wide, job-independent) for
// the Billing tab's JDE -> rate sheet map, so classifications can be mapped
// before they ever post to a job's cost history.
export const loadLaborClassifications = async (data) => {
    return await postRequest(url + 'api/readLaborClassifications', data || {})
}

export const loadJDEValues = async (data) => {
    return await postRequest(url + 'api/readJDECurrent', data)
}

export const loadHAExistsArray = async (data) => {
    return await postRequest(url + 'api/readHAExistsArray', data)
}

export const loadHEETData = async (data) => {
    return await postRequest(url + 'api/readHEETData', data)
}

export const loadHEETLabor = async (data, opts) => {
    return await postRequest(url + 'api/readHEETLabor', data, opts)
}

// Live HeavyJob time cards off the HCSS API, in readHEETLabor's row shape —
// { job, date } or { job, start, end } (≤ 31 days), or { jobs: [...] }.
// Returns { configured, data, cards, dates, adjustments, fetchedAt }; a
// missing response (function not deployed, network) comes back undefined
// and the caller falls back to HEET. See billingCardSource.js.
export const loadHeavyJobCards = async (data) => {
    return await postRequest(url + 'api/readHeavyJobCards', data)
}

// Person-per-day crew with the HeavyJob card shift ({ id, days }) — the
// Billing TV board's day/night crew chart.
export const loadCrewShifts = async (data) => {
    return await postRequest(url + 'api/readCrewShifts', data)
}

// VoltaGrid Field reports for the Billing TV board — { from, to, codeFrom }.
// Returns { days, reports, registry }; see fieldReportsBoard.js.
export const loadVgFieldBoard = async (data) => {
    return await postRequest(url + 'api/readVgFieldBoard', data)
}

// VoltaGrid Field daily reports (crew days) for one job — { job, date } or
// { job, start, end } (at most 62 days). Returns { job, from, to, days:
// [{ date, shift, foremanEmp, foremanName, labor: [{ emp, name, cls, code,
// st, ot }] }] }; see functions readVgFieldCrewDays.js and voltaGridLeadership.js.
export const loadVgFieldCrewDays = async (data) => {
    return await postRequest(url + 'api/readVgFieldCrewDays', data)
}

// HeavyJob hours per rental unit (PO-<po>-<line>) — { pos: ['231561', …] }.
// Returns { data: [{ Equipment_Code, Job_Code, Equipment_Description, Hours,
// Days, First_Date, Last_Date }] }; see billingRentalHours.js.
export const loadRentalHours = async (data) => {
    return await postRequest(url + 'api/readRentalHours', data)
}

// HeavyJob usage of rental units on a job's own cards — { job }. Returns
// { data: [{ code, po, ln, date, cc, ccDesc, hrs, desc, item, tier, poJob,
// vendor }] }, one row per unit × work date × cost code; the Billing tab
// prices rentals off it (billingRentalUsage.js).
export const loadRentalUsage = async (data) => {
    return await postRequest(url + 'api/readRentalUsage', data)
}

// Save a generated Field Ticket workbook into the job's SharePoint
// Timecards folder (02.06, Month/WE<M.D.YY> convention).
export const saveJobTicketFile = async (data) => {
    return await postRequest(url + 'api/writeJobTicketFile', data)
}

// ── Field ticket signatures (DocuSign) ──
// The Time Card Approval view sends each day's Day and Night sheets to the
// client's representative for signature and tracks what comes back. Envelopes
// and their full history live in PC.Billing_Signature_* — not in the tracker's
// Forecast_User_Data row, whose Notes blob is capped and drops old stamps.

// Envelopes + event history for one job. The endpoint refreshes anything still
// in flight from DocuSign before answering, so the tracker is current even
// when the Connect webhook is not delivering.
export const loadBillingSignatures = async (data) => {
    return await postRequest(url + 'api/readBillingSignatures', data)
}

// Open an envelope for one (work date, shift), carrying the generated field
// ticket. draft:true stops in DocuSign's draft state for preview instead of
// mailing the client immediately.
export const sendBillingSignature = async (data) => {
    return await postRequest(url + 'api/sendBillingSignature', data)
}

// send (a draft) | resend (reminder) | void (with reason) | refresh.
export const billingSignatureAction = async (data) => {
    return await postRequest(url + 'api/billingSignatureAction', data)
}

// The envelope's PDF: DocuSign's conversion of our workbook before signing,
// the signed copy with its certificate after.
export const loadBillingSignatureDocument = async (data) => {
    return await postRequest(url + 'api/billingSignatureDocument', data)
}

// Ticket Map: the packet archive in Azure Blob Storage. Every PDF the map
// builds is kept with a manifest of its inputs (fieldTicketPacket.js).
//   { action: 'put', job, docId, part, purpose, name, contentBase64, manifest }
//   { action: 'list', job, docId? }  -> { packets }   newest first
//   { action: 'get', job, id }       -> { url, manifest }
export const fieldTicketPacketStore = async (data) => {
    return await postRequest(url + 'api/fieldTicketPacket', { ...data, action: 'put' })
}
export const fieldTicketPacketList = async (data) => {
    return await postRequest(url + 'api/fieldTicketPacket', { ...data, action: 'list' })
}
export const fieldTicketPacketGet = async (data) => {
    return await postRequest(url + 'api/fieldTicketPacket', { ...data, action: 'get' })
}

// Field Ticket Reconciliation (PC.Field_Ticket_Reconciliation, see
// fieldTicketReconciliation.js): the HeavyJob values each ticket went out
// with, read back by the Billing tab's Reconciliation view.
//   capture   { job, docId?, part?, envelopeRowId?, source, by, signed?, rows } -> { snapshotId, inserted }
//   snapshots { job }                 -> { snapshots }   newest first
//   read      { job, start, end }     -> { rows }
//   record    { job, by, rows }       -> { updated }     the Invoice tab's JDE match
export const fieldTicketReconCapture = async (data) => {
    return await postRequest(url + 'api/fieldTicketReconciliation', { ...data, action: 'capture' })
}
export const fieldTicketReconSnapshots = async (data) => {
    return await postRequest(url + 'api/fieldTicketReconciliation', { ...data, action: 'snapshots' })
}
export const fieldTicketReconRead = async (data) => {
    return await postRequest(url + 'api/fieldTicketReconciliation', { ...data, action: 'read' })
}
export const fieldTicketReconRecord = async (data) => {
    return await postRequest(url + 'api/fieldTicketReconciliation', { ...data, action: 'record' })
}
// Reviews: a row looked at, with why it differs (PC.Field_Ticket_Recon_Review).
export const fieldTicketReconReviews = async (data) => {
    return await postRequest(url + 'api/fieldTicketReconciliation', { ...data, action: 'reviews' })
}
export const fieldTicketReconReview = async (data) => {
    return await postRequest(url + 'api/fieldTicketReconciliation', { ...data, action: 'review' })
}
export const fieldTicketReconUnreview = async (data) => {
    return await postRequest(url + 'api/fieldTicketReconciliation', { ...data, action: 'unreview' })
}

// Ticket Map: start Claude's review of one ticket-document cell against its
// SharePoint backup. Returns { reviewId } at once — the review runs for a
// minute or more and lands in PC.Field_Ticket_Review.
export const startFieldTicketReview = async (data) => {
    return await postRequest(url + 'api/validateFieldTicket', data)
}

// A ticket document's reviews ({ job, docId, ids? }), results parsed.
export const loadFieldTicketReviews = async (data) => {
    return await postRequest(url + 'api/readFieldTicketReviews', data)
}

export const loadTeamOpemCommitments = async (data) => {
    return await postRequest(url + 'api/readTeamOpenCommitments', data)
}

export const loadTeamOpenCommitmentsCombined = async (data) => {
    return await postRequestZip(url + 'api/readTeamTwoOpenCommitments', data)
}

export const loadBillablePhases = async (data) => {
    return await postRequest(url + 'api/readBillablePhases', data)
}

export const loadTeamBillings = async (data) => {
    return await postRequest(url + 'api/readTeamBillings', data)
}

export const loadTeamBillingsMultiple = async (data) => {
    return await postRequest(url + 'api/readTeamBillingsMultiple', data)
}

export const loadCostDetailBillings = async (data) => {
    return await postRequest(url + 'api/readCostDetailBillings', data)
}

// Live vendor-invoice match status per PO line for one job (Fact_PO_Receipt
// rollup — never versioned, invoice status keeps moving after a WIP is cut).
export const loadPOMatchStatus = async (data) => {
    return await postRequest(url + 'api/readPOMatchStatus', data)
}

// Every PO line on a job as JDE has it now (PC.v_Form_Open_PO_Items: ordered,
// received and open, received or not), for the Purchase Orders tab. The same
// endpoint closed-job mode uses; never versioned.
export const loadJobPurchaseOrdersLive = async (data) => {
    return await postRequestZip(url + 'api/readOpenCommitmentsCombinedPC', data)
}

// Email a billing summary (Excel + printable backup) to AR — fired by the
// Billing tab's Submit to AR move.
export const sendBillingSummaryEmail = async (data) => {
    return await postRequest(url + 'api/sendBillingSummary', data)
}

// Email the Daily Force Report workbook straight to the client — a plain
// email deliverable, not a signature request. Fired by the Time Card
// Approval view's Email to Client.
export const sendForceReportEmail = async (data) => {
    return await postRequest(url + 'api/sendForceReport', data)
}

// SharePoint timecard docs for a job (02 - Project Controls / 02.06 -
// Timecards on the job subsite) — the Submit to AR dialog offers them as
// evidence attachments.
export const loadJobTimecards = async (data) => {
    return await postRequest(url + 'api/readJobTimecards', data)
}

// The client's attendance roster (02.05 - Daily Force Report / Attendance &
// Force Report - Master) — who is on days and who is on nights, for the Time
// Card Approval tab's shift split.
export const loadAttendanceRoster = async (data) => {
    return await postRequest(url + 'api/readAttendanceRoster', data)
}

// Project controls' equipment log (02.02 - Tracking / PM Tracker*.xlsx,
// Equipment Log sheet) — what is on site, for the Daily Force Report's
// Equipment sheet. { job } → { found, file, rows }.
export const loadEquipmentLog = async (data) => {
    return await postRequest(url + 'api/readEquipmentLog', data)
}

// Project controls' reviewed weekly time card (02.06 - Timecards /
// Timecards_<job>_WE<m.d.yy>.xlsx) — the hours, shift and classification the
// client is actually billed, after the HeavyJob cards were checked against the
// sign-in sheets. { job, weekEnd } → { found, file, dates, labor }.
//
// Not through postRequest: that helper swallows every failure into `undefined`,
// and here "could not read" and "no card this week" must not look alike — the
// first has to stop a ticket being built, the second is an ordinary week. A 404
// is the function not being published on this service yet, which is the same
// as no card; anything else throws.
export const loadApprovedTimeCard = async (data) => {
    const res = await fetch(url + 'api/readApprovedTimeCard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
    if (res.status === 404) return { found: false, unavailable: true }
    if (!res.ok) throw new Error(`readApprovedTimeCard answered ${res.status}`)
    return await res.json()
}

// What each person was actually paid on a job, per JDE timecard row (F0618):
// { from, to, jobs: [...] } (≤ 62 days, ≤ 6 jobs) → { asof, source, rows:
// [{ id, name, job, dwk, pt, hrs, rate, gross }] }. Cost-plus labor bills at
// this wage × the Service Order multiplier — see billingInvoiceWorkbookXlsx.js.
export const loadJdePayRates = async (data) => {
    return await postRequest(url + 'api/readJdePayRates', data)
}

// JDE's JC600 (Job Transaction Detail) for one job, rebuilt live from F0911:
// { job } → { asof, source, job, rows: [[23 values, A:W]] }, dates as YYYY-MM-DD.
export const loadJobTransactionDetail = async (data) => {
    const res = await fetch(url + 'api/readJobTransactionDetail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
    const body = await res.json().catch(() => null)
    if (!res.ok) throw new Error(body?.error || `readJobTransactionDetail answered ${res.status}`)
    return body
}

// The field's labor-classification mapping (02.06 - Timecards / newest
// "Multi Phase Field Ticket" workbook, Staff List sheet) — Staff ID → the
// Tier 4 classification the person bills as. The Rates page and the ticket
// views pull this on every load so the mapping tracks the field's daily
// workbook.
export const loadFieldTicketMap = async (data) => {
    return await postRequest(url + 'api/readFieldTicketMap', data)
}

// JDE receiving-doc coordinates for a job's POs (F00165 media-object index,
// GT4311 PO-detail attachments) — the backup bundle binds the files in
// through the jdeAttachmentDoc proxy.
export const loadJdeBackupManifest = async (data) => {
    return await postRequest(url + 'api/jdeBackupManifest', data)
}

export const loadCostDetailCondensed = async (data) => {
    const endpoint = _closedJobMode ? 'api/readCostDetailCondensedPC' : 'api/readCostDetailCondensed';
    return withCostMoves(await postRequest(url + endpoint, data), data?.jobNumber)
}

export const loadUserEmailData = async (data) => {
    return await postRequest(url + 'api/readUserEmailData', data)
}

export const loadUserEmailDataSQL = async (data) => {
    return await postRequest(url + 'api/readUserEmailDataSQL', data)
}

export const loadSummaryWIP = async (data) => {
    return await postRequest(url + 'api/readSummaryBalances', data)
}

export const loadSummaryWIPArray = async (data) => {
    return await postRequest(url + 'api/readSummaryBalancesArray', data)
}

export const loadJobHardDollar = async (data) => {
    return await postRequest(url + 'api/readJobHardDollar', data)
}

export const loadBudgetHardDollar = async (data) => {
    return await postRequest(url + 'api/readBudgetHardDollar', data)
}

export const deleteUserData = async (data) => {
    //if (!data.lockedVersion) {
        // Replace caller-supplied identity with the logged-in user's identity
        // so the audit log captures who deleted the row, not the hardcoded
        // fallback the caller may have sent. See updateUserDataArray for the
        // full reasoning.
        const ident = getCurrentUserIdentity();
        const enriched = {
            ...data,
            userId: ident.userId || data?.userId || '',
            userName: ident.userName || data?.userName || '',
            userEmail: ident.userEmail || data?.userEmail || '',
        };
        return await postRequest(url + 'api/deleteUserData', enriched)
    // } else {
    //     showToast()
    // }
}

const showToast = (message = 'Current version is locked for editing', type = 'warn') => {
    showToastUI(message, type);
}

export const updateVersionInfo = async (data) => {
    return await postRequest(url + 'api/updateVersionInfo', data)
}

export const loadJobBillings = async (data) => {
    //console.log('read billings', data)
    const endpoint = _closedJobMode ? 'api/readBillingsPC' : 'api/readBillings';
    return await postRequest(url + endpoint, data)
}

export const loadJobBillingsCombined = async (data) => {
    //console.log('read billings', data)
    const endpoint = _closedJobMode ? 'api/readBillingsCombinedPC' : 'api/readBillingsCombined';
    return await postRequest(url + endpoint, data)
}

export const loadJobOpenCommitments = async (data) => {
    return await postRequest(url + 'api/readOpenCommitments', data)
}

export const loadJobOpenCommitmentsCombined = async (data) => {
    const endpoint = _closedJobMode ? 'api/readOpenCommitmentsCombinedPC' : 'api/readOpenCommitmentsCombined';
    return await postRequestZip(url + endpoint, data)
}

// Cost history reads go through withCostMoves (costMoves.js): cost waiting on a
// JDE job cost transfer reads on the code it is moving to.
export const loadCostHistory = async (data) => {
    const endpoint = _closedJobMode ? 'api/readCostDetailPC' : 'api/readCostDetail';
    return withCostMoves(await postRequest(url + endpoint, data), data?.id)
}

export const loadCostHistoryCombined = async (data) => {
    const endpoint = _closedJobMode ? 'api/readCostDetailCombinedPC' : 'api/readCostDetailCombined';
    return withCostMoves(await postRequestZip(url + endpoint, data), data?.id)
}

export const loadJobBillingsTeam = async (data) => {
    //console.log('read billings', data)
    return await postRequest(url + 'api/readBillingsTeam', data)
}

export const loadJobOpenCommitmentsTeam = async (data) => {
    return await postRequest(url + 'api/readOpenCommitmentsTeam', data)
}

// Portfolio-wide procurement views. Both accept { forecastGuid, teams? , ids? } —
// omit teams and ids for the whole company. Scope is resolved in SQL rather than
// by shipping thousands of job ids; see functions/src/sql/readUncommittedRollup.sql.
export const loadUncommittedRollup = async (data) => {
    return await postRequest(url + 'api/readUncommittedRollup', data)
}

export const loadProcurementDates = async (data) => {
    return await postRequest(url + 'api/readProcurementDates', data)
}

export const loadCostHistoryTeam = async (data) => {
    return await postRequest(url + 'api/readCostDetailTeam', data)
}

export const loadPhaseCodes = async (data) => {
    return await postRequest(url + 'api/readPhaseCodes', data)
}

export const createExcelData = async (data) => {
    console.log('create excel - yes')
    //return await postRequest(url + 'api/createExcelData', data)
    return await fetch(url + 'api/createExcelData', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'responseType': 'blob'
        },
        body: JSON.stringify(data)
    }).then(async (res) => {
        const data = await res.json();
        return data
    }).catch(err => {
        console.log('err', err)
    })
}

export const loadVersionTeamJob = async (data) => {
    return await postRequest(url + 'api/readVersionTeamJob', data)
}

//this one would fail ocasionally, so I broke it out
export const loadOverheadPhaseEquipment = async (data) => {
    return await postRequest(url + 'api/readOverheadPhaseEquipment', data)
}

export const loadJobs = async (data) => {
    return await postRequest(url + 'api/readJobInfo', data)
}

export const loadJobsClosed = async (data) => {
    return await postRequest(url + 'api/readJobClosedInfo', data)
}

export const loadJobsAll = async (data) => {
    return await postRequest(url + 'api/readJobInfoAll', data || {})
}

export const loadJobsNew = async (data) => {
    return await postRequest(url + 'api/readJobInfoNew', data)
}

export const loadTeams = async (data) => {
    return await postRequest(url + 'api/readTeamInfo', data)
}

export const loadK2Data = async (data) => {
    return await postRequest(url + 'api/readK2', data)
}

// Bulk field-leadership + contract-value roster (one row per job) for the
// Team Schedule Gantt tab. Frontend joins on Job_Number = BU_ID.
export const loadJobRoster = async (data) => {
    return await postRequest(url + 'api/readJobRoster', data || {})
}

// Staffed labor windows per job (one row per mobilization) for the Team
// Schedule Gantt — a job with three separate crews on site returns three rows.
// { versionGuid, gapDays } are optional; the query defaults to the newest
// version with burn-tool rows and a 14-day gap tolerance.
export const loadJobLaborIntervals = async (data) => {
    return await postRequest(url + 'api/readJobLaborIntervals', data || {})
}

// ─── Daily Field Report ──────────────────────────────────────────────────────
// Whole-report save: header + all section row arrays in one call (Draft or
// Submitted). Server assigns ReportNumber on first save.
export const saveFieldReport = async (data) => {
    return await postRequest(url + 'api/saveFieldReport', data)
}

export const loadFieldReports = async (data) => {
    return await postRequest(url + 'api/readFieldReports', data)
}

export const loadFieldReport = async (data) => {
    return await postRequest(url + 'api/readFieldReport', data)
}

// Prior installs by cost code (+ pipe size) for the Pipeline quantity cards:
// to-date / last-7-day sums, latest budget qty per code, latest pipe sizes.
export const loadFieldReportQtyHistory = async (data) => {
    return await postRequest(url + 'api/readFieldReportQtyHistory', data)
}

// Equipment context for the Equipment picker: owned units from Burn Tool
// actuals plus rental PO lines with the rental return date.
export const loadFieldReportEquipment = async (data) => {
    return await postRequest(url + 'api/readFieldReportEquipment', data)
}

// One job-level equipment mob/demob row (confirm the date or correct it).
// Saved per job + piece, not per report, so it carries onto later days.
export const saveFieldReportEquipmentDates = async (data) => {
    return await postRequest(url + 'api/saveFieldReportEquipmentDates', data)
}

export const deleteFieldReport = async (data) => {
    return await postRequest(url + 'api/deleteFieldReport', data)
}

// ── Schedule & Baseline tab ─────────────────────────────────────────────────
// One read for the whole tab: upload history plus the active schedule's
// activities and the active productivity baseline's rows.
export const loadProjectSchedule = async (data) => {
    return await postRequest(url + 'api/readProjectSchedule', data)
}

// Company-wide weekly-update recency, one row per active job. No parameters —
// see functions/src/sql/readScheduleStatus.sql.
export const loadScheduleStatus = async () => {
    return await postRequest(url + 'api/readScheduleStatus', {})
}

// Schedule file (P6 .xer / P6 XML / MS Project XML) as base64.
export const uploadProjectSchedule = async (data) => {
    return await postRequest(url + 'api/uploadProjectSchedule', data)
}

// Productivity baseline workbook (Excel/CSV) as base64.
export const uploadProductivityBaseline = async (data) => {
    return await postRequest(url + 'api/uploadProductivityBaseline', data)
}

// Planned production curve per cost code. Each save writes a new version and
// deactivates the previous one.
export const saveQuantitySchedule = async (data) => {
    return await postRequest(url + 'api/saveQuantitySchedule', data)
}

export const deleteScheduleImport = async (data) => {
    return await postRequest(url + 'api/deleteScheduleImport', data)
}

export const deleteFieldReportPhoto = async (data) => {
    return await postRequest(url + 'api/deleteFieldReportPhoto', data)
}

// Raw-bytes upload (postRequest JSON-encodes, so photos need their own fetch).
// docType: 'photo' (default) | 'timeTicket' | 'progressReport' — the latter two
// are sub documents uploaded from the Labor & Equipment step.
export const uploadFieldReportPhoto = async ({ reportId, buId, caption, location, uploadedBy, contentType, blob, docType }) => {
    const params = new URLSearchParams({
        report_id: reportId,
        bu_id: buId,
        caption: caption || '',
        location: location || '',
        uploaded_by: uploadedBy || '',
        content_type: contentType || 'image/jpeg',
        doc_type: docType || 'photo',
    });
    return await fetch(url + 'api/fieldReportUploadPhoto?' + params.toString(), {
        method: 'POST',
        headers: { 'Content-Type': contentType || 'image/jpeg' },
        body: blob,
    }).then(async (res) => {
        if (!res.ok) throw new Error('Upload failed (' + res.status + ')');
        return await res.json();
    })
}

// ─── Overview hero banner (blob storage, one per job) ────────────────────────
// readJobBanner → { sasUrl | null }; uploadJobBanner takes raw JPEG bytes
// (postRequest JSON-encodes, so it needs its own fetch) → { sasUrl }.
export const readJobBanner = async (data) => {
    return await postRequest(url + 'api/readJobBanner', data)
}
export const updateJobBannerView = async (data) => {
    return await postRequest(url + 'api/updateJobBannerView', data)
}
export const deleteJobBanner = async (data) => {
    return await postRequest(url + 'api/deleteJobBanner', data)
}
export const uploadJobBanner = async ({ buId, uploadedBy, blob }) => {
    const params = new URLSearchParams({ bu_id: buId, uploaded_by: uploadedBy || '' });
    return await fetch(url + 'api/uploadJobBanner?' + params.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'image/jpeg' },
        body: blob,
    }).then(async (res) => {
        if (!res.ok) throw new Error('Upload failed (' + res.status + ')');
        return await res.json();
    })
}

// ─── Pour Cards (Concrete Pour Approval) ─────────────────────────────────────
// All three take { buId, jobName, card } — the pour-card JSON row the Quality
// step edits. The PDF endpoint returns raw bytes, so it needs its own fetch.
export const downloadPourCardPdf = async (data) => {
    return await fetch(url + 'api/pourCardPdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    }).then(async (res) => {
        if (!res.ok) throw new Error('PDF failed (' + res.status + ')');
        return await res.blob();
    }).catch((err) => {
        console.log('err', err)
        return null;
    })
}

export const archivePourCard = async (data) => {
    return await postRequest(url + 'api/pourCardArchive', data)
}

export const emailPourCard = async (data) => {
    return await postRequest(url + 'api/pourCardEmail', data)
}

export const loadBudgetExists = async (data) => {
    return await postRequest(url + 'api/readBudgetExists', data)
}

// ─── Job Team ────────────────────────────────────────────────────────────────
// Role assignments per job (PC.Job_Team); people come from JDE's employee
// address book (dbo.Dim_Staff) via readStaffDirectory.
export const loadStaffDirectory = async () => {
    return await postRequest(url + 'api/readStaffDirectory', {})
}

export const loadJobTeam = async (data) => {
    return await postRequest(url + 'api/readJobTeam', data)
}

export const loadJobTeamDefaults = async (data) => {
    return await postRequest(url + 'api/readJobTeamDefaults', data)
}

export const saveJobTeamMember = async (data) => {
    return await postRequest(url + 'api/saveJobTeamMember', data)
}

export const deleteJobTeamMember = async (data) => {
    return await postRequest(url + 'api/deleteJobTeamMember', data)
}

export const loadBudgetExistsArray = async (data) => {
    return await postRequest(url + 'api/readBudgetExistsArray', data)
}

export const loadAuditLog = async (data) => {
    return await postRequest(url + 'api/readAuditLog', data)
}

// Records a login into PC.Forecast_Audit_Login, grouped per person per day.
// Replaces the old per-load 'Test' tab write into the versioned forecast table.
export const recordLogin = async (data) => {
    return await postRequest(url + 'api/recordLogin', data)
}

// Records an action that is not a row edit (an export) in PC.Forecast_Audit_Log,
// so the Audit Log page shows who did it. { actionType: 'EXPORT', userEmail,
// userName, userId, buId, version, tab, fieldName, newValue }.
export const recordAudit = async (data) => {
    return await postRequest(url + 'api/recordAudit', data)
}

// Returns every third-party-tab row that shares a natural key with another row
// (i.e. duplicate groups), for the Data Cleanup page. { buId, versionGuid } are
// optional filters. Deletion of a specific copy goes through deleteUserData.
export const loadForecastDuplicates = async (data) => {
    return await postRequest(url + 'api/readForecastDuplicates', data)
}

export const sendEmailError = async (data) => {
    return await postRequest(url + 'api/sendEmailError', data)
}

export const createVersion = async (data) => {
    return await postRequest(url + 'api/createVersion', data)
}

export const deleteDayZero = async (data) => {
    return await postRequest(url + 'api/deleteDayZero', data)
}

export const deleteCO = async (data) => {
    try {
        const result = await postRequest(url + 'api/deleteCO', data);
        return result || {}; // Always return an object, even if result is undefined/null
    } catch (error) {
        console.log('deleteCO error:', error);
        return {}; // Return empty JSON on error
    }
}

export const deleteCOByRowLabel = async (data) => {
    try {
        const result = await postRequest(url + 'api/deleteCOByRowLabel', data);
        return result || {};
    } catch (error) {
        console.log('deleteCOByRowLabel error:', error);
        return {};
    }
}

export const updateCOStatus = async (data) => {
    return await postRequest(url + 'api/updateCOStatus', data)
}

export const createExcelFile = async (data) => {
    //console.log('checkIfContingencyLoaded')
    console.log('passed data', data)
    const a = await fetch('http://127.0.0.1:4000/createexcel', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: data })
    }).then(async (res) => {
        const data = await res.json();
        console.log('job data', data)
        // if(data && data.length > 0) {
        //     const jobRows = []
        //     data.forEach(element => {
        //         BUs.push(element.Cat_Code_BU_21)
        //     });
        //storeData('jobBillings', {[id]: data});
        return data
        //}
        //storeData('businessUnits', data);
    }).catch(err => {
        console.log('err', err)
    })

    return a
}

export const f = (v) => {
    return { formattedValue: v, value: v, floatValue: parseFloat(v) }
}

export const fNum = (value, round = null) => {
    const NEGATION_FORMAT_REGEX = /^\((.*)\)$/;
    let v = value
    let hasNegation = false;

    if (typeof v === "number") {
        hasNegation = v < 0;
        v = hasNegation ? v * -1 : v;
    } else if (v?.[0] === "-") {
        hasNegation = true;
        v = v.substring(1);
    } else if (v?.match(NEGATION_FORMAT_REGEX)) {
        hasNegation = true;
        v = v.replace(NEGATION_FORMAT_REGEX, "$1");
    }
    v = typeof v == 'number' ? v.toFixed(2) : (parseFloat(v)).toFixed(2)

    return numericFormatter(v + '', {
        prefix: hasNegation ? "(" : "",
        suffix: hasNegation ? ")" : "",
        thousandSeparator: ",",
        allowNegative: true,
        decimalScale: round ? round == 2 ? 1 : 0 : 2
    })
}

export const pNum = (value) => {
    const NEGATION_FORMAT_REGEX = /^\((.*)\)$/;
    let v = value
    let hasNegation = false;
    if (typeof v === "number") {
        hasNegation = v < 0;
        v = hasNegation ? v * -1 : v;
    } else if (v?.[0] === "-") {
        hasNegation = true;
        v = v.substring(1);
    } else if (v?.match(NEGATION_FORMAT_REGEX)) {
        hasNegation = true;
        v = v.replace(NEGATION_FORMAT_REGEX, "$1");
    }

    const n = numericFormatter(v + '', {
        prefix: hasNegation ? "-" : "",
        decimalSeparator: ".",
        thousandSeparator: ",",
        allowNegative: true,
        decimalScale: 2
    })

    const o = isNaN(parseFloat(n)) ? 0 : parseFloat(n.replace(/,/g, ''))
    return o ? o : 0
}

export const sum = (array) => {
    let total = 0;
    for (var num of array) total += num;
    return total;
}

export const cN = (value) => {
    return isNaN(value) ? 0 : value
}

export const updateUserDataArray = async (data, userdata = null) => {
    // Rows per POST. Prod writes about 40 ms a row, so 1,000 rows is under a
    // minute, well inside the 230 s Azure gives one request.
    const batchSize = 1000;
    if (!Array.isArray(data)) {
        data = [data]; // Convert single item to an array
    }

    // Replace caller-supplied identity with the logged-in user's identity.
    // Most call sites hardcode 'Seth Bailey' / '154045' as a fallback, which
    // leaks into the audit log when the jotai store isn't populated yet or
    // when a user isn't in the Forecast_User SQL table (e.g. Meghan). We
    // unconditionally overwrite when we have a real identity so those
    // hardcodes can never reach the audit table.
    const ident = getCurrentUserIdentity();
    data = data.map(row => ({
        ...row,
        userId: ident.userId || row?.userId || '',
        userName: ident.userName || row?.userName || '',
        userEmail: ident.userEmail || row?.userEmail || '',
    }));

    // Detect if URL does NOT contain 'prod' for non-production environment
    const notProd = !url.toLowerCase().includes('prod');

    // Helper function to recursively split and retry batches
    // Returns an array of resolved Forecast_User_Data_GUIDs aligned to `batch`
    // order (null where unknown). Callers use this to write the new GUID back
    // into their row so subsequent edits take the UPDATE-by-GUID path.
    const processBatch = async (batch, attempt = 0) => {
        if (!batch.length) return [];
        if (batch[0].lockedVersion) {
            console.log('locked version')
            showToast();
            return batch.map(() => null);
        }
        try {
            const json = await fetch(url + 'api/updateUserDataArray', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-ZUMO-AUTH': data?.auth,
                    'X-Not-Prod': String(notProd)
                },
                body: JSON.stringify({ data: batch, notProd })
            }).then(async (res) => {
                if (!res.ok) {
                    showToastUI(`Couldn't save (status ${res.status}). We'll keep trying.`, 'error');
                    throw new Error(`Network response was not ok: ${res.status}`);
                }
                return await res.json();
            });
            // Server returns { guids: [{ index, guid }] } keyed by batch position.
            const guidList = json?.guids || [];
            return batch.map((_, i) => {
                const hit = guidList.find(g => g && Number(g.index) === i);
                return hit ? hit.guid : null;
            });
        } catch (error) {
            // If batch has more than one row, split and retry
            if (batch.length > 1) {
                const mid = Math.floor(batch.length / 2);
                const a = await processBatch(batch.slice(0, mid));
                const b = await processBatch(batch.slice(mid));
                return [...a, ...b];
            } else if (attempt < 2) {
                // A lone row usually fails on something transient (SQL deadlock victim
                // when several saves land at once) — rerun it before giving up.
                await new Promise(r => setTimeout(r, 400 * (attempt + 1) + Math.random() * 300));
                return processBatch(batch, attempt + 1);
            } else {
                // Log or handle the single problematic row
                console.error('Failed to update row due to SQL error:', batch[0], error);
                return [null];
            }
        }
    };

    console.log('updateUserDataArray', data);

    //Truncate string fields to their SQL max lengths
    const fieldLengths = {
        fieldName: 50,
        rowId: 80,
        value: 1024,
        costType: 10,
        costCode: 10,
        vendor: 50,
        po: 10,
        equipmentTier: 50,
        id: 10,
        description: 255,
        notes: 1024,
        resourceCode: 50,
        status: 50,
        rowLabel: 255
    };

    data = data.map(item => {
        const newItem = { ...item };
        const now = new Date().toISOString();
        // Stamp created date if not already set, and updated datetime on every change
        if (!newItem.DSS_Created_Date) {
            newItem.DSS_Created_Date = now;
        }
        newItem.DSS_Updated_Datetime = now;
        Object.keys(fieldLengths).forEach(field => {
            if (typeof newItem[field] === 'string' && newItem[field].length > fieldLengths[field]) {
                newItem[field] = newItem[field].substring(0, fieldLengths[field]);
            }
        });
        return newItem;
    });
    //console.log('data after truncation', data);

    const results = [];
    for (let i = 0; i < data.length; i += batchSize) {
        const batch = data.slice(i, i + batchSize);
        const r = await processBatch(batch);
        results.push(...(r || []));
    }
    // Aligned to input order; each entry is the resolved GUID or null.
    return results;
}

// No-op — writes are sent immediately via updateUserDataArray; kept for
// call-sites that invoke it on beforeunload / unmount as a safety flush.
export const flushWriteQueue = () => {}

// ── SOP / Document Library ──

export const sopListDocuments = async () => {
    return await fetch(url + 'api/sop/documents').then(r => r.json()).catch(err => ({ error: err.message }));
}

export const sopDeleteDocument = async (sourceFileId) => {
    return await fetch(url + 'api/sop/documents/' + encodeURIComponent(sourceFileId), { method: 'DELETE' })
        .then(r => r.json()).catch(err => ({ error: err.message }));
}

export const sopUploadDocument = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return await fetch(url + 'api/sop/upload', { method: 'POST', body: formData })
        .then(r => r.json()).catch(err => ({ error: err.message }));
}

export const sopSharePointList = async (path) => {
    return await postRequest(url + 'api/sop/sharepoint/list', { path });
}

export const sopSharePointSync = async (files) => {
    return await postRequest(url + 'api/sop/sharepoint/sync', { files });
}

export const sopSyncStatus = async () => {
    return await fetch(url + 'api/sop/sharepoint/status').then(r => r.json()).catch(err => ({ error: err.message }));
}

export const sopChat = async (query, model, history) => {
    return await fetch(url + 'api/sop/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, model, history }),
    });
}

export const sopModels = async () => {
    return await fetch(url + 'api/sop/models').then(r => r.json()).catch(err => ({ error: err.message }));
}

